import {IPendingLoginExternalLinkHandler} from "./pending-login.external-link-handler.interface";
import {
    ExternalLinkInIframeHandlerBase,
    ExternalLinkInIframeRendererParams
} from "../external-link-in-iframe-handler-base";
import {GNWP_SOURCES} from "../../../gnwp/gnwp-consts";
import {ExternalLinksZIndexes} from "../../external-links-z-indexes";
import {CIPropRead} from "../../../../../utils/case-insensitive-property-reader";
import {IOpenPendingLoginOptions} from "../../external-links.service.interface";
import {NullableUndefinedNumber, NullableUndefinedString} from "../../../../../common/types/nullable-types";
import {CyClass} from "../../../../../common/cy-classes";
import {
    AreaType,
    ControlCenterDeeplink,
    ControlCenterOpenSource,
    IChannel,
    IControlCenterFullSpecificParams
} from "@sparkware/uc-sdk-core";
import {CHANNEL_ELEMENTS, CHANNEL_SOURCE} from "../../../../../common/const/casinofied-analytics-consts";

interface ITokenAndOriginInformation {
    token: NullableUndefinedString;
    sourceProductPackage: NullableUndefinedNumber
}

export class PendingLoginExternalLinkHandler extends ExternalLinkInIframeHandlerBase implements IPendingLoginExternalLinkHandler {

    async openPendingLogin(options: IOpenPendingLoginOptions): Promise<void> {
        const url = await this._getGnwpUrl(options);
        await this._showExternalLinkDialog(url);
    }

    protected async _getGnwpUrl(options: IOpenPendingLoginOptions): Promise<string> {
        return await (await this.services.gnwp).getPendingLoginUrl({
            token: options.token
        });
    }

    protected _getGnwpSource(): GNWP_SOURCES {
        return GNWP_SOURCES.PENDING_LOGIN;
    }

    protected get _hideCloseButton(): boolean {
        return true;
    }

    protected _getIframeRendererParams(): ExternalLinkInIframeRendererParams {
        return {
            maxWidth: 1014,
            maxHeight: 702,
            zIndex: ExternalLinksZIndexes.PendingAction,
            httpVerb: 'POST',
            analyticsAction: this._getGnwpSource(),
            hideCloseButton: this._hideCloseButton,
            iframeExtraCssClass: CyClass('cy-pending-login-external-link-container')
        };
    }

    //we need to cache the autologin promise because pending login can send us for example '101' and 'pending' one after the other
    //so in order NOT to start 2 parallel autologin we will execute only the first one and all the other will wait for the same promise
    private _autologinPromise: null | Promise<void> = null;

    protected async _onPostMessageReceived(message: MessageEvent): Promise<void> {
        const actionId = this._extractActionId(message);
        if(!actionId) {
            return;
        }



        const createAutologinPromise = async () => {
            try {
                if (!this.services.user.isAuthorized) {
                    let options = this._extractTokenAndOriginInformation(message);
                    if(options.token) {
                        try {
                            await this.services.user.runAutoLogin(options);
                        } finally {
                            this._autologinPromise = null;
                        }

                    }
                }
            } catch(e) {
                this.services.logger.error("Failed to autologin on pending login action with action ID 7", e);
            }
        }

        const runAutologin = async () => {
            if(!this._autologinPromise) {
                this._autologinPromise = createAutologinPromise();
            }

            await this._autologinPromise;
        }

        switch (actionId.toLowerCase()) {
            case 'pending':
                this._closeExternalLinkDialog();
                await runAutologin();
                break;
            case '21':
                this._closeExternalLinkDialog();
                await runAutologin();
                await (await this.services.externalLinks).openMyAccount({
                    tabId: this._extractMyAccountTabId(message)
                });
                break;
            case '73':
                await this._openLoginForm();
                break;
            case '75':
                await this._openUploadDocuments(this._extractTokenAndOriginInformation(message));
                break;
            case '101': // ControlCenter
                await runAutologin();
                const channel = this._extractChannel(message);
                const appSpecificParameters = this._extractAppSpecificParameters(message);
                await this._openControlCenter({channel: channel, appSpecificParameters: appSpecificParameters});
                break;
            case '7':
                this._closeExternalLinkDialog();

                await runAutologin();

                await this._openQuickDeposit({
                    promCode: this._extractPromoCode(message),
                    channel: {
                        source: CHANNEL_SOURCE.pendingAction,
                        area: AreaType.Header,
                        element: CHANNEL_ELEMENTS.depositButton
                    }
                });
                break;
        }
    }


    protected _extractTokenAndOriginInformation(message: MessageEvent): ITokenAndOriginInformation {
        const messageData = this._parseMessageData(message);
        const token = CIPropRead.getStringProperty('Token', messageData);
        if(!token) {
            return {
                token: null,
                sourceProductPackage: null
            };
        }

        const originInformation = CIPropRead.getProperty('originInformation', messageData);
        const sourceProductPackage = CIPropRead.getNumericProperty('originProductPackageID', originInformation);

        return {
            token: token,
            sourceProductPackage: sourceProductPackage
        };

    }

    protected async _openLoginForm(): Promise<void> {
        this._closeExternalLinkDialog();
        await this.services.user.openLogin();
    }

    protected async _openUploadDocuments(options: ITokenAndOriginInformation): Promise<void> {
        this._closeExternalLinkDialog();

        if(options.token) {
            await this.services.user.runAutoLogin({
                token: options.token,
                sourceProductPackage: options.sourceProductPackage
            });
            await (await this.services.externalLinks).openUploadDocumentFromMyAccount();
        } else {
            this.services.logger.error(`Received open upload documents event from pending login but they didn't provide a token`)
        }
    }

        //todo duplicate code with pending-action.external-link-handler.ts  -need to be shared
    protected async _openControlCenter(options: {channel: IChannel; appSpecificParameters: Partial<IControlCenterFullSpecificParams>;}): Promise<void> {
        this._closeExternalLinkDialog();
        await (await this.services.appLauncher).openControlCenterFull({
            channel: {
                source: CHANNEL_SOURCE.pendingAction,
                area: AreaType.button,
                ...options.channel
            },
            businessCorrelationID: this.services.tools.getGuid(),
            appSpecificParameters: {
                source: ControlCenterOpenSource.Lobby,
                isIncreaseLimitsDisabled: true,
                deeplink: ControlCenterDeeplink.Main,
                ...options.appSpecificParameters
            }
        })
    }
}